import React, { useState } from 'react';
import Box from '@material-ui/core/Box';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import { CALL_VERBIAGES_MAP } from 'features/ai-generators/constants';
import strings from 'strings';
import { FPActionButton, FPIconButton, When } from 'UI/components/atoms';
import AutocompleteSelect from 'UI/components/molecules/AutocompleteSelect';
import { Endpoints } from 'UI/constants/endpoints';
import { CandidateEntity } from 'UI/constants/entityTypes';
import { SvgArrowLeft } from 'UI/res';

import { useConversationSearchModalStyles } from './EntitySelectionModal.styled';
import { COPIES, ROLEPLAY_CALL_TARGET_OPTIONS, ROLEPLAY_SETUP_OPTIONS } from './Roleplay.constants';

const SETUP_OPTIONS = Object.keys(ROLEPLAY_SETUP_OPTIONS);

export const EntitySelectionModal = ({ onClose, onSelect, open }) => {
  const classes = useConversationSearchModalStyles();
  const [entityType, setEntityType] = useState(ROLEPLAY_CALL_TARGET_OPTIONS[0]);
  const [selectedRoleplaySetup, setSelectedRoleplaySetup] = useState(null);
  const [entity, setEntity] = useState(null);
  const hasSelectedSetupOption = selectedRoleplaySetup !== null;
  const isFromDirectory = selectedRoleplaySetup === ROLEPLAY_SETUP_OPTIONS.fromInventory;

  const handleClose = (_event, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;

    onClose && onClose();
  };

  const handleSelectEntity = (_name, value) => {
    setEntity(value);
  };

  const handleSelectEntityType = (_name, value) => {
    setEntityType(value);
    setEntity(null);
  };

  const handleConfirm = () => {
    const isCandidateEntity = entityType?.id === CandidateEntity.id;

    const callType = isCandidateEntity
      ? CALL_VERBIAGES_MAP.roleplayColdCallToHa
      : CALL_VERBIAGES_MAP.roleplayColdCallToCandidate;
    onSelect && onSelect({ entity, selectedRoleplaySetup, callType });
    handleClose();
  };

  const handleSelectSetupOption = newSetupOption => {
    setSelectedRoleplaySetup(newSetupOption);
  };

  const handleBackClick = () => {
    setSelectedRoleplaySetup(null);
    setEntity(null);
  };

  return (
    <Dialog
      classes={{ paper: classes.dialogPaper }}
      fullWidth
      maxWidth="md"
      onClose={handleClose}
      open={open}
      scroll="paper"
    >
      <Box className={classes.header}>
        <Box alignItems="center" display="flex" flexDirection="row" gridGap={8}>
          {hasSelectedSetupOption && (
            <FPIconButton
              icon={SvgArrowLeft}
              iconMode="custom"
              onClick={handleBackClick}
              size="sm"
            />
          )}
          <Typography variant="h6">{COPIES.modal.title}</Typography>
        </Box>
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent className={classes.dialogContent}>
        {!hasSelectedSetupOption && (
          <>
            <Typography variant="subtitle1">{COPIES.modal.subtitle}</Typography>
            <Box alignItems="center" display="flex" flexDirection="row" gridGap={16} marginTop={3}>
              {SETUP_OPTIONS.map(setupOption => (
                <Box
                  key={setupOption}
                  className={classes.optionContainer}
                  component="button"
                  onClick={() => handleSelectSetupOption(setupOption)}
                >
                  <Typography variant="subtitle1">
                    {COPIES.modal.setupOptions[setupOption].title}
                  </Typography>
                  <Typography variant="body2">
                    {COPIES.modal.setupOptions[setupOption].subtitle}
                  </Typography>
                </Box>
              ))}
            </Box>
          </>
        )}

        {hasSelectedSetupOption && (
          <>
            <Typography variant="subtitle1">
              {COPIES.modal.setupOptions[selectedRoleplaySetup].title}
            </Typography>

            <AutocompleteSelect
              defaultOptions={ROLEPLAY_CALL_TARGET_OPTIONS}
              disableClearable
              freeSolo={false}
              name="entityType"
              onSelect={handleSelectEntityType}
              placeholder={COPIES.modal.whoAreYouCallingPlaceholder}
              selectedValue={entityType}
            />

            <When condition={isFromDirectory}>
              <AutocompleteSelect
                name="entity"
                onSelect={handleSelectEntity}
                placeholder={entityType.placeholder}
                renderOption={entityType.renderOption}
                selectedValue={entity}
                typeahead
                typeaheadLimit={50}
                typeaheadParams={{ entityType: entityType.id }}
                url={Endpoints.Search}
              />
            </When>
          </>
        )}
      </DialogContent>

      <When condition={hasSelectedSetupOption}>
        <DialogActions>
          <FPActionButton
            onClick={handleClose}
            text={strings.shared.ui.cancel}
            variant="outlined"
          />
          <FPActionButton
            disabled={isFromDirectory && !entity}
            onClick={handleConfirm}
            text={strings.shared.ui.continue}
          />
        </DialogActions>
      </When>
    </Dialog>
  );
};
